import React, { useState, useEffect } from "react";
import {
  DataGridPremium as DataGrid,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridCellParams,
  GridColDef,
  GridColType,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridPaginationModel,
  GridRowParams,
  GridSortDirection,
  GridSortItem,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  getDefaultGridFilterModel,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  gridFilterModelSelector,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import { tokenOptions, SortInput, DUMMY_MUTATION } from "../../backend/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { backendURL } from "../../constants";
import { asUTC, formatDate, formatDateTime } from "../../tools";
import {
  Box,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BUTTON_HEIGHT } from "./buttonHeight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { v4 as uuidv4, validate } from "uuid";
import { AlertProps } from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ActionButton from "./ActionButton";
import { gridDeleteSx } from "./gridActionSx";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";

export type GraphQLGridField = {
  field: string;
  label: string;
  visible?: boolean;
  flex?: number;
  width?: number;
  defaultSort?: "asc" | "desc";
  type?: GridColType;
  valueGetter?: any;
  renderCell?: any;
  editable?: boolean;
  editableForNew?: boolean;
  valueOptions?: string[];
};

export type GraphQLActionParams = {
  id: any;
  row: any;
  navigate: (path: string) => void;
  refetch: () => void;
};

// A page that can save the grid's current view: the button's label, and what to do with the filter
// and the visible columns (a JSON array of field names) when it is pressed — the page asks for the
// rest (a name, a section) and saves.
export type GraphQLSaveQueryParameters = {
  label: string;
  onSave: (filter: string, columns: string) => void;
};

export type GraphQLGridParameters = {
  graphqlQuery: any;
  graphqlUpdate?: any;
  graphqlAdd?: any;
  graphqlDelete?: any;
  graphqlFilter?: string;
  graphqlSaveQuery?: GraphQLSaveQueryParameters | undefined;
  // A field to look rows up by, offered as a search box in the toolbar: a plain "contains" on that
  // one column, for the thing people arrive knowing. Left unset on a grid with no such column.
  quickSearchField?: string;
  // A saved query's visible columns (JSON array of field names). Restores column visibility on
  // replay; absent/empty/unparsable falls back to the fields' default `visible` flags.
  savedColumns?: string;
  // When replaying a saved query, lets the columns be updated in place (id + the mutation), so the
  // tile's columns can be changed without re-saving the whole query. `refetch` reloads the saved
  // query afterwards so its stored columns stay in sync. Only wire it up for admins.
  graphqlUpdateColumns?: { id: string; mutation: any; refetch?: () => void };
  graphqlItemsPath: (arg: any) => any;
  graphqlUpdatePath?: (arg: any) => any;
  graphqlAddPath?: (arg: any) => any;
  fields: GraphQLGridField[];
  rowHeight?: number;
  getActions?: (params: any) => React.ReactElement<GridActionsCellItemProps>[];
  // The most buttons getActions returns for any row, which sizes the actions column. The delete
  // button the grid adds itself is counted on top.
  maxActions?: number;
  allRows?: boolean;
};

export type GraphQLGridProps = {
  parameters: GraphQLGridParameters;
  // Give the grid its own section-card look (border + rounded corners + shadow) when it stands alone
  // on a page, so it needs no wrapping Paper.
  elevated?: boolean;
};

const notEqualOperator: GridFilterOperator = {
  label: "not equal to",
  value: "!=",
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) {
      return null;
    }
    return ({ value }: GridCellParams) => value !== filterItem.value;
  },
  InputComponent: getGridStringOperators()[0].InputComponent,
};

const predicateToKeepOperators = (operatorNames: string[]) => {
  return (gridFilterOperator: GridFilterOperator) =>
    operatorNames.includes(gridFilterOperator.value);
};

const predicateToRemoveOperators = (operatorNames: string[]) => {
  return (gridFilterOperator: GridFilterOperator) =>
    !operatorNames.includes(gridFilterOperator.value);
};

const getFilterOperators = (field: GraphQLGridField) => {
  const fieldType = field.type || "string";
  switch (fieldType) {
    case "uuid":
      return getGridStringOperators().filter(
        predicateToKeepOperators(["equals"]),
      );
    case "image":
      return getGridStringOperators().filter(
        predicateToKeepOperators(["isEmpty", "isNotEmpty"]),
      );
    case "string":
      return [
        ...getGridStringOperators().filter(
          predicateToRemoveOperators(["isAnyOf"]),
        ),
        notEqualOperator,
      ];
    case "number":
      return getGridNumericOperators().filter(
        predicateToRemoveOperators(["isAnyOf"]),
      );
    case "boolean":
      return getGridBooleanOperators();
    case "dateTime":
    case "dateTimeShort":
      return getGridDateOperators().filter(
        predicateToRemoveOperators(["isEmpty", "isNotEmpty"]),
      );
    case "singleSelect":
      return getGridSingleSelectOperators().filter(
        predicateToRemoveOperators(["isAnyOf"]),
      );
    default:
      console.log("Unknown type: " + field.type);
      return undefined;
  }
};

// The services store their timestamps in UTC without a zone, so a date column reads them as UTC;
// an empty one stays empty rather than becoming 1970.
const getValueGetter = (type: string) => {
  if (type === "dateTime" || type === "dateTimeShort") {
    return (params: any) =>
      params.value ? new Date(asUTC(params.value)) : null;
  }
  return undefined;
};

const renderImage = (imageID: string, rowHeight: number) => {
  if (!imageID) return null;
  return (
    <img
      alt=""
      loading="lazy"
      style={{ objectFit: "contain" }}
      width={rowHeight}
      height={rowHeight}
      src={`${backendURL}/image/thumbnail/${imageID}`}
    />
  );
};

const renderCell = (inParams: any, type: string) => {
  if (type === "image") {
    return (params: any) => renderImage(params.value, inParams.rowHeight);
  }
  // Dates read YYYY-MM-DD across the app, with HH:mm when the column keeps the time.
  if (type === "dateTimeShort") {
    return (params: any) => formatDate(params.value);
  }
  if (type === "dateTime") {
    return (params: any) => formatDateTime(params.value);
  }
  return undefined;
};

const getMapType = (type: string) => {
  if (type === "image") {
    return "string";
  }
  if (type === "dateTimeShort") {
    return "dateTime";
  }
  return type;
};

const getFlex = (
  type: GridColType | undefined,
  flex: number,
  width: number | undefined,
) => {
  if (width !== undefined) return undefined;
  switch (type) {
    case "boolean":
    case "number":
    case "dateTime":
    case "dateTimeShort":
    case "image":
      return undefined;
    default:
      return flex;
  }
};

const getWidth = (
  type: GridColType | undefined,
  width: number | undefined,
  rowHeight: number | undefined,
) => {
  if (width !== undefined) return width;
  switch (type) {
    case "boolean":
      return 60;
    case "number":
      return 120;
    case "dateTime":
      return 200;
    case "dateTimeShort":
      return 100;
    case "image":
      return (rowHeight || 64) + 15;
    default:
      return width;
  }
};

// The width an actions-column button takes, its spacing included.
const ACTION_BUTTON_WIDTH = 40;

const getColumns = (
  params: GraphQLGridParameters,
  refetch: any,
  deleteItem: any,
  navigate: any,
  setSnackbar: any,
  confirm: any,
  apiRef: any,
) => {
  const baseColumns = params.fields.map((field: GraphQLGridField) => {
    return {
      field: field.field,
      headerName: field.label,
      flex: getFlex(field.type, field.flex || 1, field.width),
      width: getWidth(field.type, field.width, params.rowHeight),
      type: getMapType(field.type || "string"),
      valueGetter: field.valueGetter || getValueGetter(field.type || "string"),
      filterOperators: getFilterOperators(field),
      renderCell:
        field.renderCell || renderCell(params, field.type || "string"),
      editable: field.editable || field.editableForNew || false,
      valueOptions: field.valueOptions,
    };
  });

  if (!params.getActions && !params.graphqlDelete) {
    return baseColumns;
  }

  const getActions = ({ id, row }: GridRowParams<any>) => {
    let actions: React.ReactElement<GridActionsCellItemProps>[] = [];
    if (params.graphqlDelete) {
      actions.push(
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          sx={gridDeleteSx}
          onClick={async () => {
            try {
              await confirm({
                description: "Are you sure you want to delete?",
                confirmationText: "Delete",
              });
              await deleteItem({ variables: { id: id } });
              setSnackbar({
                children: "Deleted successfully.",
                severity: "success",
              });
              apiRef.current.updateRows([{ id: id, _action: "delete" }]);
              await refetch();
            } catch (error) {
              setSnackbar({ children: "Unable to delete.", severity: "error" });
            }
          }}
        />,
      );
    }
    if (params.getActions) {
      params
        .getActions({ id: id, navigate: navigate, refetch: refetch, row: row })
        .forEach((action) => {
          actions.push(action);
        });
    }
    return actions;
  };
  // Room for every button a row can have, side by side.
  const mostButtons =
    (params.graphqlDelete ? 1 : 0) +
    (params.getActions ? (params.maxActions ?? 1) : 0);
  const actionColumn: GridColDef = {
    field: "actions",
    type: "actions",
    headerName: "Actions",
    width: ACTION_BUTTON_WIDTH * mostButtons,
    getActions: getActions,
  };
  return [...baseColumns, actionColumn];
};

const getDefaultSort = (params: GraphQLGridParameters) => {
  const fieldsWithDefaultSort = params.fields.filter(
    (field) => field.defaultSort !== undefined,
  );
  const gridSortItems: GridSortItem[] = fieldsWithDefaultSort.map((field) => ({
    field: field.field,
    sort: field.defaultSort as GridSortDirection,
  }));
  return gridSortItems;
};

// The visible field names from a saved query, keeping only those the grid still has (so a renamed
// or dropped column is silently ignored). Returns null when there is nothing usable — absent,
// empty, not JSON, not an array, or every name unknown — so the caller falls back to the default.
export const parseSavedColumns = (
  saved: string | undefined,
  fields: GraphQLGridField[],
): string[] | null => {
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return null;
    const known = new Set(fields.map((field) => field.field));
    const names = parsed.filter(
      (name: any) => typeof name === "string" && known.has(name),
    );
    return names.length > 0 ? names : null;
  } catch {
    return null;
  }
};

const getInitialState = (params: GraphQLGridParameters) => {
  const savedColumns = parseSavedColumns(params.savedColumns, params.fields);

  const columnVisibilityModel = params.fields.reduce(
    (acc: any, field: GraphQLGridField) => {
      if (savedColumns) {
        // Restore what was saved: only the named fields are visible.
        acc[field.field] = savedColumns.includes(field.field);
      } else if (field.visible === false) {
        // No saved columns — keep today's default visibility.
        acc[field.field] = false;
      }
      return acc;
    },
    {},
  );

  return {
    columns: {
      columnVisibilityModel: columnVisibilityModel,
    },
  };
};

// A column on a related object ("color.system") is sorted by the flat key the backend offers for it
// ("colorSystem"): a sort input takes no paths, and quietly ignores a key it does not have. The
// backend names its UseSort keys after this rule, so changing it breaks those sorts silently — see
// "Sorting a column on a related object" in CODING_STANDARDS.md.
const mapSortField = (field: string) =>
  field
    .split(".")
    .map((part, i) => (i === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join("");

const getSorting = (sortModel: GridSortModel) => {
  const sort: SortInput = {};
  sortModel.forEach((sortItem) => {
    sort[mapSortField(sortItem.field)] = sortItem.sort?.toUpperCase() as
      | "ASC"
      | "DESC";
  });
  return [sort];
};

const getFilters = (
  filterModel: GridFilterModel | undefined,
  params: GraphQLGridParameters,
) => {
  if (filterModel === undefined || filterModel?.items.length === 0) {
    return params?.graphqlFilter;
  }

  const mapValue = (value: any, field: string) => {
    const columnType =
      params?.fields.find((column) => column.field === field)?.type || "string";

    switch (columnType) {
      case "boolean":
      case "number":
        return value?.toString();
      case "dateTime":
        return '"' + value?.toString() + 'T00:00:00Z"';
      default:
        return '"' + value?.toString() + '"';
    }
  };

  const mapField = (field: string, operator: string) => {
    if (field === "id") return "iD";
    if (
      field.endsWith("ID") &&
      operator !== "isEmpty" &&
      operator !== "isNotEmpty"
    )
      return field.substring(0, field.length - 2) + ".iD";
    return field;
  };

  const convertFilterItemToGraphQLFilter = (gridFilterItem: GridFilterItem) => {
    const { field, operator, value } = gridFilterItem;
    const mappedField = mapField(field, operator);
    const columnType =
      params?.fields.find((column) => column.field === field)?.type || "string";
    switch (operator) {
      case "startsWith":
      case "endsWith":
      case "contains":
        return `${mappedField}.${operator}(${mapValue(value, field)})`;
      case "equals":
      case "=":
      case "is":
        return `${mappedField} == ${mapValue(value, field)}`;
      case "not equals":
      case "is not":
      case "not":
      case "!=":
        return `${mappedField} != ${mapValue(value, field)}`;
      case ">":
      case "after":
        return `${mappedField} > ${mapValue(value, field)}`;
      case ">=":
      case "onOrAfter":
        return `${mappedField} >= ${mapValue(value, field)}`;
      case "<":
      case "before":
        return `${mappedField} < ${mapValue(value, field)}`;
      case "<=":
      case "onOrBefore":
        return `${mappedField} <= ${mapValue(value, field)}`;
      case "isEmpty":
        return columnType === "string"
          ? `(${mappedField} == null || ${mappedField} == "")`
          : `${mappedField} == null`;
      case "isNotEmpty":
        return columnType === "string"
          ? `(${mappedField} != null && ${mappedField} != "")`
          : `${mappedField} != null`;
      case "isAnyOf":
        return `${mappedField}.isAny([${value.map((v: any) => mapValue(v, field)).join(", ")}])`;
      default:
        return operator;
    }
  };

  const modelStrings = filterModel?.items
    .filter((item) => {
      const fieldDef = params.fields.find(
        (field) => field.field === item.field,
      );
      if (!fieldDef) return false;

      // filters on type uuid need a valid uuid value to compare against
      if (fieldDef.type === "uuid") return validate(item.value);

      // a filter with operator isAnyOf must have values to be valid
      if (item.operator === "isAnyOf")
        return Array.isArray(item.value) && item.value.length > 0;

      // filters with operators isEmpty and isNotEmpty do not need a value
      if (item.operator === "isEmpty" || item.operator === "isNotEmpty")
        return true;

      // all other operators need a value
      return item.value;
    })
    .map(convertFilterItemToGraphQLFilter);

  const modelString = modelStrings?.join(
    filterModel?.logicOperator === "or" ? " || " : " && ",
  );
  const filters = [
    params?.graphqlFilter,
    modelString && modelString !== "" && "(" + modelString + ")",
  ];
  const result = filters
    .filter((item) => item !== undefined && item !== "")
    .join(" && ");
  console.log(result);
  return result;
};

export type CustomToolbarProps = {
  parameters: GraphQLGridParameters;
  setSnackbar: any;
  quickSearch: string;
  setQuickSearch: (value: string) => void;
};

const CustomToolbar = ({
  parameters,
  setSnackbar,
  quickSearch,
  setQuickSearch,
}: CustomToolbarProps) => {
  const apiRef = useGridApiContext();
  const withNew = parameters.graphqlAdd !== undefined;
  const withSaveQuery = parameters.graphqlSaveQuery;
  const withUpdateColumns = parameters.graphqlUpdateColumns;
  const token = useAppSelector((state) => state.auth.token);
  const [updateColumns] = useMutation(
    withUpdateColumns?.mutation || DUMMY_MUTATION,
    tokenOptions(token),
  );

  // The columns visible right now, in display order, as a JSON array of field names — read from the
  // live grid state (not the fields' defaults) so a column the user turned on or off is captured.
  const currentVisibleColumns = () => {
    const known = new Set(parameters.fields.map((field) => field.field));
    const visibleFields = apiRef.current
      .getVisibleColumns()
      .map((column: any) => column.field)
      .filter((field: string) => known.has(field));
    return JSON.stringify(visibleFields);
  };

  // Update just the current saved query's columns, leaving the filter as-is.
  const handleSaveColumns = async () => {
    if (withUpdateColumns) {
      const result = await updateColumns({
        variables: {
          id: withUpdateColumns.id,
          columns: currentVisibleColumns(),
        },
      });
      if (result?.data) {
        setSnackbar({ children: "Columns saved.", severity: "success" });
        // Reload the saved query so its stored columns match what was just written.
        withUpdateColumns.refetch?.();
      } else {
        setSnackbar({ children: "Unable to save columns.", severity: "error" });
      }
    }
  };

  const handleNew = async () => {
    const id = uuidv4();
    apiRef.current.updateRows([{ id: id, _isNew: true }]);
    const firstField = apiRef.current.getVisibleColumns()[0].field;
    apiRef.current.startRowEditMode({ id: id, fieldToFocus: firstField });
  };

  const handleSaveQuery = () => {
    if (withSaveQuery) {
      const filterModel = gridFilterModelSelector(apiRef);
      const filter = getFilters(filterModel, parameters);
      if (filter) {
        // The columns visible right now, so replaying the query restores this view.
        withSaveQuery.onSave(filter, currentVisibleColumns());
      }
    }
  };
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      {parameters.quickSearchField && (
        <TextField
          size="small"
          placeholder={`Search ${parameters.quickSearchField.toUpperCase()}`}
          value={quickSearch}
          onChange={(event) => setQuickSearch(event.target.value)}
          sx={{
            width: 200,
            // The toolbar's own blue on white, as a pill no taller than the buttons beside it —
            // the height comes from buttonHeight so a row of controls reads as one band.
            "& .MuiOutlinedInput-root": {
              height: BUTTON_HEIGHT.small,
              borderRadius: 999,
              bgcolor: "common.white",
              color: "primary.main",
              fontSize: "0.8125rem",
              "& fieldset": {
                borderColor: (theme: any) =>
                  alpha(theme.palette.primary.main, 0.5),
              },
              "&:hover fieldset": { borderColor: "primary.main" },
              "&.Mui-focused fieldset": { borderColor: "primary.main" },
            },
            "& .MuiSvgIcon-root": { color: "primary.main" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: quickSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="clear"
                  onClick={() => setQuickSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      )}

      <Box sx={{ flexGrow: 1 }} />

      {withNew && <ActionButton onClick={handleNew}>Add</ActionButton>}
      {withSaveQuery && (
        <ActionButton onClick={handleSaveQuery}>
          {withSaveQuery.label}
        </ActionButton>
      )}
      {withUpdateColumns && (
        <ActionButton onClick={handleSaveColumns}>Save columns</ActionButton>
      )}
    </GridToolbarContainer>
  );
};

export const GraphQLDataGrid = ({ parameters, elevated }: GraphQLGridProps) => {
  const confirm = useConfirm();
  const token = useAppSelector((state) => state.auth.token);
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] =
    useHistoryState<GridPaginationModel>("paginationModel", {
      page: 0,
      pageSize: 10,
    });
  const effectivePaginationModel = parameters.allRows
    ? { page: 0, pageSize: 16000 }
    : paginationModel;
  const [sortModel, setSortModel] = useHistoryState<GridSortModel>(
    "sortModel",
    getDefaultSort(parameters),
  );
  const [filterModel, setFilterModel] = useHistoryState<GridFilterModel>(
    "filterModel",
    getDefaultGridFilterModel(),
  );
  // What is typed, and what is being searched with: the box settles into the query after a beat, so
  // a five-character SKU is one request rather than five.
  const [quickSearch, setQuickSearch] = useHistoryState("quickSearch", "");
  const [effectiveSearch, setEffectiveSearch] = useState(quickSearch);
  useEffect(() => {
    const settle = setTimeout(() => setEffectiveSearch(quickSearch), 400);
    return () => clearTimeout(settle);
  }, [quickSearch]);

  // The search is one more clause on the filter the grid already builds — composed here rather than
  // inside getFilters, which stays about the columns the user filtered by hand.
  const quickSearchFilter =
    parameters.quickSearchField && effectiveSearch
      ? `${parameters.quickSearchField}.contains("${effectiveSearch.replace(/"/g, "")}")`
      : undefined;
  const filter = [getFilters(filterModel, parameters), quickSearchFilter]
    .filter((clause) => clause !== undefined && clause !== "")
    .join(" && ");

  // Filtering by hand is a fresh question, so the quick search steps out of the way — cleared here
  // with the model, in one go, so the grid is not queried once for the filter and again for the box.
  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    setQuickSearch("");
    setEffectiveSearch("");
  };

  const { loading, error, data, refetch } = useQuery(parameters.graphqlQuery, {
    ...tokenOptions(token),
    variables: {
      take: effectivePaginationModel.pageSize,
      skip: effectivePaginationModel.page * effectivePaginationModel.pageSize,
      filter,
      sort: getSorting(sortModel),
    },
  });
  const [sendUpdate] = useMutation(
    parameters.graphqlUpdate || DUMMY_MUTATION,
    tokenOptions(token),
  );
  const [addItem] = useMutation(
    parameters.graphqlAdd || DUMMY_MUTATION,
    tokenOptions(token),
  );
  const [deleteItem] = useMutation(
    parameters.graphqlDelete || DUMMY_MUTATION,
    tokenOptions(token),
  );
  const path = data && parameters.graphqlItemsPath(data);
  const [snackbar, setSnackbar] = useState<Pick<
    AlertProps,
    "children" | "severity"
  > | null>(null);

  const handleCloseSnackbar = () => setSnackbar(null);

  const processUpdate = async (updatedRow: any, originalRow: any) => {
    try {
      if (originalRow._isNew) {
        const result = await addItem({ variables: updatedRow });
        const addedItem =
          result?.data && parameters.graphqlAddPath?.(result.data);
        const success = !!addedItem;

        if (!success) {
          throw new Error();
        }

        // the id of addedItem will not match the id of originalRow/updatedRow because for the former it's generated by the backend
        // while for the latter it's generated on the frontend when creating a new row. We need to delete the row in the grid with
        // the old id and return the new row
        apiRef.current.updateRows([{ id: originalRow.id, _action: "delete" }]);
        setSnackbar({ children: "Saved successfully.", severity: "success" });
        return addedItem;
      } else {
        const result = await sendUpdate({ variables: updatedRow });
        const updatedItem =
          result?.data && parameters.graphqlUpdatePath?.(result.data);
        const success = !!updatedItem;

        if (!success) {
          throw new Error();
        }

        setSnackbar({ children: "Saved successfully.", severity: "success" });
        return updatedItem;
      }
    } catch (error: any) {
      setSnackbar({ children: "Unable to save.", severity: "error" });
      if (originalRow._isNew) {
        return { id: originalRow.id, _action: "delete" };
      } else {
        return originalRow;
      }
    }
  };

  if (error) {
    console.log(error);
    return <p>Error :(</p>;
  }

  return (
    <>
      <DataGrid
        autoHeight
        sx={
          elevated
            ? { border: 1, borderColor: "divider", borderRadius: 1 }
            : undefined
        }
        apiRef={apiRef}
        editMode="row"
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            parameters: parameters,
            setSnackbar: setSnackbar,
            quickSearch: quickSearch,
            setQuickSearch: setQuickSearch,
          },
        }}
        pagination
        pageSizeOptions={[10, 25, 50, 100, 500, { label: "All", value: 16000 }]}
        rows={path?.items || []}
        columns={getColumns(
          parameters,
          refetch,
          deleteItem,
          navigate,
          setSnackbar,
          confirm,
          apiRef,
        )}
        initialState={getInitialState(parameters)}
        rowCount={path?.totalItems || 0}
        loading={loading}
        paginationModel={effectivePaginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sortingMode="server"
        disableMultipleColumnsSorting
        disableDensitySelector
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowHeight={parameters.rowHeight}
        disableRowSelectionOnClick
        processRowUpdate={processUpdate}
        isCellEditable={(params) => {
          const fieldDef = parameters.fields.find(
            (f) => f.field === params.field,
          );
          if (!fieldDef) return false;
          if (params.row._isNew && fieldDef.editableForNew) return true;
          return fieldDef.editable || false;
        }}
      />
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </>
  );
};
